/**
 * A reusable 5-Whys + Selected Root Cause block (CR-7353).
 *
 * The Root Cause Analysis was split into an Occurrence chain and an Escape
 * chain, each with its own Whys and Selected Root Cause. This renders one
 * such chain against whatever it is pointed at (the Occurrence chain uses
 * whys / selected_root_cause; the Escape chain uses escape_whys /
 * escape_selected_root_cause).
 *
 * CR-7139: a chain starts as five Whys, but the user may add Whys (up to
 * MAX_WHYS) or remove them (down to one); the rest renumber in order.
 */

import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, X } from 'lucide-react';
import { NCREditFormData } from './types';
import { chainWhys, MAX_WHYS, whyFieldName, WhyChain } from './whyChain';

interface FiveWhysSectionProps {
  /** Section heading, e.g. "Occurrence Root Cause Analysis". Omitted when the
   *  Escape chain is off: the single chain then renders inline, as it did
   *  before CR-7353. */
  title?: string;
  /** Which chain: `whys` (Occurrence) or `escape_whys` (Escape). */
  chain: WhyChain;
  /** The selected-root-cause field name. */
  selectedField: string;
  /** When true, Why 1 and Selected Root Cause show `*` and validate (CR-7069
   *  keeps only those two required per chain). */
  required?: boolean;
  formData: NCREditFormData;
  fieldErrors: Record<string, string>;
  editable: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
}

export function FiveWhysSection({
  title,
  chain,
  selectedField,
  required = false,
  formData,
  fieldErrors,
  editable,
  onUpdateFormData,
}: FiveWhysSectionProps) {
  const errorTextareaClass =
    'border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500';
  const renderError = (field: string, message?: string) =>
    message ? (
      <p id={`${field}-error`} role="alert" className="text-sm text-red-600 mt-1">
        {message}
      </p>
    ) : null;

  const whys = chainWhys(formData, chain);
  const setWhys = (next: string[]) => onUpdateFormData(chain, next);

  return (
    <div className={title ? 'border-t pt-4 mt-4 space-y-4' : 'space-y-4'}>
      {title && <h4 className="text-sm font-semibold">{title}</h4>}

      {whys.map((why, i) => {
        const field = whyFieldName(chain, i + 1);
        // A required-field error stands only while its box is still empty.
        const error = why.trim() ? undefined : fieldErrors[field];
        return (
          <div key={i}>
            <div className="flex items-center justify-between">
              <Label>
                Why {i + 1}?{i === 0 && required && ' *'}
              </Label>
              {editable && whys.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-muted-foreground"
                  aria-label={`Remove Why ${i + 1}`}
                  onClick={() => setWhys(whys.filter((_, j) => j !== i))}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <Textarea
              value={why}
              onChange={(e) => setWhys(whys.map((w, j) => (j === i ? e.target.value : w)))}
              disabled={!editable}
              rows={2}
              maxLength={500}
              aria-invalid={!!error}
              aria-describedby={error ? `${field}-error` : undefined}
              className={error ? errorTextareaClass : ''}
            />
            {renderError(field, error)}
          </div>
        );
      })}

      {editable && whys.length < MAX_WHYS && (
        <Button type="button" variant="outline" size="sm" onClick={() => setWhys([...whys, ''])}>
          <Plus className="h-4 w-4 mr-1" />
          Add Why
        </Button>
      )}

      <div>
        <Label>Selected Root Cause{required && ' *'}</Label>
        <Textarea
          value={(formData[selectedField] as string) || ''}
          onChange={(e) => onUpdateFormData(selectedField, e.target.value)}
          disabled={!editable}
          rows={3}
          maxLength={500}
          aria-invalid={!!fieldErrors[selectedField]}
          aria-describedby={fieldErrors[selectedField] ? `${selectedField}-error` : undefined}
          className={fieldErrors[selectedField] ? errorTextareaClass : ''}
        />
        {renderError(selectedField, fieldErrors[selectedField])}
      </div>
    </div>
  );
}
